import type { AsyncThunkPayloadCreator } from '@reduxjs/toolkit';
import cloneDeep from 'lodash/cloneDeep';
import isObject from 'lodash/isObject';

import { AccountRoutes } from '@webapp/account/src/resources/routes';
import type { States } from '@webapp/account/src/resources/states';

import { Alert } from '@webapp/common/components/message';
import { API_URL } from '@webapp/common/conf';
import { ApiRespCode, isSubdomain } from '@webapp/common/lib/api';
import { getShareToken, getSurveyTokenCookie, getUserToken } from '@webapp/common/lib/cookies';
import { getFormattedParams, ttFetch } from '@webapp/common/lib/fetch';
import { LocalStorage, LocalStorageKeys } from '@webapp/common/lib/storage';

const badToken = new Set([ApiRespCode.ERR_TOKEN_CODE, ApiRespCode.ERR_BAD_TOKEN]);

const goAuth = (reason: string): void => {
    // TODO redirect with action
    const auth = window.location.pathname.startsWith(AccountRoutes.auth.root);

    if (!auth || window.location.pathname === AccountRoutes.authConfirmDeviceRequest.link) {
        if (!auth) {
            LocalStorage.set(
                LocalStorageKeys.BACK_URL,
                `backUrl=${encodeURIComponent(window.location.pathname)}` +
                    (reason ? `&reason=${encodeURIComponent(reason)}` : ''),
            );
        }
        AccountRoutes.authLogin.assign();
    }
};

const successCallback = (response) => {
    if (typeof response !== 'object') {
        console.error('successCallback invalid response type:', response);
        return Promise.reject(response);
    }
    const { code, message, status } = response;

    if (code === ApiRespCode.ERR_STATUS_CODE || code === ApiRespCode.OK_CODE) {
        return Promise.resolve(response);
    }

    if (status === 401 || badToken.has(code)) {
        return Promise.reject(goAuth(response.url.includes('get/profile') ? '' : message));
    }

    return Promise.reject(response);
};

const makeRequest = ({
    method,
    body,
    headers,
    path,
    params,
    absPath,
    callback,
}: {
    method: string;
    path: string;
    headers?: Record<string, string>;
    body?: any;
    params?: Record<string, any>;
    absPath?: boolean;
    callback?: (resp) => void;
}): Promise<any> => {
    const url = absPath ? `${API_URL}${path}` : `${API_URL}/core/v1/${path}`; // TODO purge,check start/results

    const formattedParams = getFormattedParams(params);

    return ttFetch({
        method,
        retries: 1,
        headers: {
            'Content-Type': 'application/json',
            accept: 'application/json',
        },
    })(`${url}${formattedParams}`, {
        method: 'POST',
        headers,
        body,
    }).then(callback || successCallback);
};

export const post = (
    path: string,
    data: JsonValue = {},
    config?: {
        absPath?: boolean;
        callback?: (resp) => void;
        params?: Record<string, any>;
        withoutToken?: boolean;
        json?: boolean;
    },
): Promise<any> => {
    const token = getUserToken();
    const shareToken = getShareToken();
    let body;

    data = cloneDeep(data) || {};

    if (token && !config?.withoutToken) {
        data['token'] = token;
    }

    if (shareToken && !config?.withoutToken) {
        data['shareToken'] = shareToken;
    }

    if (config?.json) {
        body = data;
    } else {
        body = new FormData();
        for (const [key, val] of Object.entries(data)) {
            const value = val instanceof File ? val : isObject(val) ? JSON.stringify(val) : (val as string);

            if (value !== undefined) {
                body.append(key, value);
            }
        }
    }

    return makeRequest({
        method: 'POST',
        body,
        path,
        params: config?.params,
        absPath: config?.absPath,
        callback: config?.callback,
        headers: {
            ...(isSubdomain && { 'X-Authorization': getSurveyTokenCookie() }),
        },
    });
};

export const DELETE = (path: string, absPath = false): Promise<any> => {
    const token = getUserToken();

    return makeRequest({
        method: 'DELETE',
        path,
        absPath,
        headers: {
            'T-Authorization': token,
        },
    });
};

export const GET = (
    path: string,
    params?: Record<string, any>,
    config?: {
        absPath?: boolean;
        callback?: (resp) => void;
    },
): Promise<any> => {
    const token = getUserToken();
    const shareToken = getShareToken();

    const newParams = params;

    if (shareToken) {
        newParams['shareToken'] = shareToken;
    }

    return makeRequest({
        method: 'GET',
        path,
        params: newParams,
        absPath: config?.absPath,
        headers: {
            'T-Authorization': token || '',
        },
    });
};

export const patchOrPut =
    (method: string) =>
    (path: string, data: JsonValue = {}, params?: Record<string, any>, absPath = false): Promise<any> => {
        const token = getUserToken();
        data = cloneDeep(data) || {};

        if (token) {
            data['token'] = token;
        }

        const body = new FormData();

        for (const [key, val] of Object.entries(data)) {
            const value = val instanceof File ? val : isObject(val) ? JSON.stringify(val) : (val as string);

            if (value !== undefined) {
                body.append(key, value);
            }
        }

        return makeRequest({
            method,
            body,
            path,
            params,
            absPath,
            headers: {
                ...(isSubdomain && { 'X-Authorization': getSurveyTokenCookie() }),
                'T-Authorization': token,
            },
        });
    };

export const POST = post;
export const PATCH = patchOrPut('PATCH');
export const PUT = patchOrPut('PUT');

export const wrapApiCall =
    <Args, Returned = AnyObject, ThunkApiConfig = unknown>(
        payloadCreator: AsyncThunkPayloadCreator<
            Returned & { message?: string },
            Args,
            ThunkApiConfig & { state: States }
        >,
        { notify = true, notifyError = true }: { notify?: boolean | string; notifyError?: boolean | string } = {},
    ) =>
    async (args, thunkAPI) => {
        const { requestId } = thunkAPI;
        notify && Alert.saving(undefined, requestId);
        try {
            const resp: any = await payloadCreator(args, thunkAPI);
            if (notify) {
                const message = typeof notify === 'string' ? notify : resp.message;
                Alert.success(message, requestId);
            }
            return resp;
        } catch ({ code, message, status }) {
            if (notifyError) {
                message = typeof notifyError === 'string' ? notifyError : message;
                console.error(message);
                Alert.error(message, requestId);
            }
            return thunkAPI.rejectWithValue({ code, message, status });
        }
    };

export const isRespSuccess = (resp) => resp?.payload?.status === 200;
